import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { watchLocations, createLocation, updateLocation, deleteLocation, backfillLocationsFromJobs, mergeLocations } from "../../services/firestore";
import { findDuplicateGroups } from "../../services/locationIdentity";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import PhoneInput from "../../components/PhoneInput";
import { formatPhone } from "../../utils/phone";
import "./OwnerLayout.css";

const TYPES = ["All","Retail","Private","Airport","Hub","Office","Other"];

const TYPE_CHIPS = {
  Retail:  "chip-blue",
  Private: "chip-purple",
  Airport: "chip-teal",
  Hub:     "chip-amber",
  Office:  "chip-gray",
  Other:   "chip-gray",
};

export default function Locations() {
  const { userProfile } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [showAdd, setShowAdd]     = useState(false);
  const [selected, setSelected]   = useState(null);

  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState("");
  const [reviewingDupes, setReviewingDupes] = useState(false);
  // Hand-picked merge: owner ticks any locations on the list, then reviews
  // them as one group in the same keep/merge screen the auto-flagged
  // duplicates use.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked]       = useState(() => new Set());
  const [manualGroup, setManualGroup] = useState(null);

  function togglePick(loc) {
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(loc.id)) next.delete(loc.id); else next.add(loc.id);
      return next;
    });
  }

  function stopSelecting() {
    setSelecting(false);
    setPicked(new Set());
  }

  // Likely-duplicate groups (same Place ID, identical name+address, same
  // coordinates, or identical address). Memoized — it's an O(n²) scan.
  const dupeGroups = useMemo(() => findDuplicateGroups(locations), [locations]);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsub = watchLocations(userProfile.companyId, (data) => {
      setLocations(data);
      setLoading(false);
    });
    return unsub;
  }, [userProfile]);

  async function handleBackfill() {
    if (!userProfile?.companyId) return;
    if (!window.confirm(
      "Scan every past job and import their stops into Locations? " +
      "Only run this once — running it multiple times will inflate job counts."
    )) return;
    setBackfilling(true);
    setBackfillMsg("");
    try {
      const { scanned, created } = await backfillLocationsFromJobs(userProfile.companyId);
      setBackfillMsg(`Scanned ${scanned} stop${scanned !== 1 ? "s" : ""}, ${created} imported.`);
    } catch (e) {
      setBackfillMsg("Backfill failed. Check console.");
      console.error(e);
    }
    setBackfilling(false);
  }

  const filtered = locations.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch = !search
      || l.name?.toLowerCase().includes(q)
      || l.address?.toLowerCase().includes(q)
      || l.customerName?.toLowerCase().includes(q);
    const matchType = typeFilter === "All" || l.type === typeFilter;
    return matchSearch && matchType;
  });

  // Group filtered locations by customerName
  const groups   = {};
  const ungrouped = [];
  filtered.forEach((l) => {
    if (l.customerName) {
      if (!groups[l.customerName]) groups[l.customerName] = [];
      groups[l.customerName].push(l);
    } else {
      ungrouped.push(l);
    }
  });
  const sortedGroupKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));

  if (manualGroup) {
    return (
      <DuplicateReview
        companyId={userProfile?.companyId}
        groups={[manualGroup]}
        manual
        onBack={() => { setManualGroup(null); stopSelecting(); }}
      />
    );
  }

  if (reviewingDupes) {
    return (
      <DuplicateReview
        companyId={userProfile?.companyId}
        groups={dupeGroups}
        onBack={() => setReviewingDupes(false)}
      />
    );
  }

  if (selected) {
    return (
      <LocationDetail
        location={selected}
        onBack={() => setSelected(null)}
        onSave={async (id, data) => {
          await updateLocation(id, data);
          setLocations((ls) => ls.map((l) => l.id === id ? { ...l, ...data } : l));
          setSelected((l) => ({ ...l, ...data }));
        }}
        onDelete={async (id) => {
          await deleteLocation(id);
          // Drop from local state and bounce back to the list. The
          // watchLocations subscription will reconcile soon anyway, but
          // this avoids a stale flash of the deleted row.
          setLocations((ls) => ls.filter((l) => l.id !== id));
          setSelected(null);
        }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Locations</div>
          <div className="page-subtitle">{locations.length} location{locations.length !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {backfillMsg && (
            <span style={{ fontSize: 11, color: "#2d7a2d" }}>{backfillMsg}</span>
          )}
          <button className="btn btn-sm" onClick={handleBackfill} disabled={backfilling}
            title="One-time sweep — imports stops from every past job">
            {backfilling ? "Importing..." : "Import from jobs"}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => setReviewingDupes(true)}
            disabled={dupeGroups.length === 0}
            title="Find and merge duplicate locations"
            style={dupeGroups.length > 0
              ? { background: "#fff7e6", borderColor: "#f0c46a", color: "#8a5a00", fontWeight: 700 }
              : undefined}
          >
            {dupeGroups.length > 0 ? `🔀 Merge duplicates (${dupeGroups.length})` : "No duplicates"}
          </button>
          {selecting ? (
            <>
              <button
                className="btn btn-sm btn-blue"
                disabled={picked.size < 2}
                onClick={() => setManualGroup(locations.filter((l) => picked.has(l.id)))}
              >
                Merge {picked.size} selected
              </button>
              <button className="btn btn-sm" onClick={stopSelecting}>Cancel</button>
            </>
          ) : (
            <button className="btn btn-sm" onClick={() => setSelecting(true)}
              title="Tick any locations on the list and merge them into one">
              Select to merge
            </button>
          )}
          <button className="btn btn-blue" onClick={() => setShowAdd(true)}>+ Add location</button>
        </div>
      </div>

      <div className="page-body">
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <input
            style={{ width: 280 }}
            placeholder="Search by name, customer, or address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {TYPES.map((t) => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={`btn btn-sm ${typeFilter === t ? "btn-blue" : ""}`}>{t}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Loading locations...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📍</div>
            <div>{search || typeFilter !== "All" ? "No locations match" : "No locations yet"}</div>
            {!search && typeFilter === "All" && (
              <button className="btn btn-blue" onClick={() => setShowAdd(true)}>Add your first location</button>
            )}
          </div>
        ) : (
          <div className="panel">
            {/* Header */}
            <div style={{
              display: "grid", gridTemplateColumns: `${selecting ? "24px " : ""}1fr 180px 90px 60px`,
              padding: "7px 14px", background: "#f8f8f8",
              borderBottom: "1px solid #e8e8e8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              {selecting && <span />}
              <span>Name · Address</span>
              <span>Contact</span>
              <span>Type</span>
              <span>Jobs</span>
            </div>

            {/* Grouped rows */}
            {sortedGroupKeys.map((customerName) => (
              <React.Fragment key={customerName}>
                {/* Customer group header */}
                <div style={{
                  padding: "6px 14px 4px",
                  background: "#f0f4fa",
                  borderBottom: "1px solid #e8e8e8",
                  fontSize: 10, fontWeight: 800, color: "#185FA5",
                  textTransform: "uppercase", letterSpacing: 0.6,
                }}>
                  {customerName}
                  <span style={{ marginLeft: 6, fontWeight: 400, color: "#888", textTransform: "none" }}>
                    {groups[customerName].length} address{groups[customerName].length !== 1 ? "es" : ""}
                  </span>
                </div>
                {groups[customerName].map((loc) => (
                  <LocationRow key={loc.id} loc={loc} onSelect={selecting ? togglePick : setSelected}
                    selecting={selecting} checked={picked.has(loc.id)} indented />
                ))}
              </React.Fragment>
            ))}

            {/* Ungrouped rows */}
            {ungrouped.map((loc) => (
              <LocationRow key={loc.id} loc={loc} onSelect={selecting ? togglePick : setSelected}
                selecting={selecting} checked={picked.has(loc.id)} />
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddLocationModal
          companyId={userProfile?.companyId}
          onClose={() => setShowAdd(false)}
          onCreated={(loc) => { setLocations((ls) => [loc, ...ls]); setShowAdd(false); }}
        />
      )}
    </div>
  );
}

function LocationRow({ loc, onSelect, indented, selecting, checked }) {
  // Show first contact (legacy contactName falls back if no contacts[]
  // array exists yet) plus a count badge if there are more on file.
  const contacts = Array.isArray(loc.contacts) ? loc.contacts : [];
  const primary = contacts[0] || (loc.contactName ? { name: loc.contactName, phone: loc.contactPhone } : null);
  const extraCount = Math.max(0, contacts.length - 1);

  return (
    <div
      onClick={() => onSelect(loc)}
      style={{
        display: "grid", gridTemplateColumns: `${selecting ? "24px " : ""}1fr 180px 90px 60px`,
        padding: "10px 14px",
        paddingLeft: indented ? 28 : 14,
        borderBottom: "1px solid #f0f0f0",
        alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer",
        background: checked ? "#eef5ff" : "",
      }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = "#fafbfc"; }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = ""; }}
    >
      {selecting && <input type="checkbox" checked={!!checked} readOnly style={{ pointerEvents: "none" }} />}
      <div>
        <div style={{ fontWeight: 600 }}>{loc.name}</div>
        <div style={{ fontSize: 10, color: "#888" }}>{loc.address || "—"}</div>
      </div>
      <div style={{ fontSize: 11, color: "#666" }}>
        {primary ? (
          <>
            <div>
              {primary.name || "—"}
              {extraCount > 0 && (
                <span style={{ marginLeft: 5, color: "#185FA5", fontSize: 9, fontWeight: 700 }}>
                  +{extraCount} more
                </span>
              )}
            </div>
            {primary.phone && <div style={{ color: "#888" }}>{formatPhone(primary.phone)}</div>}
          </>
        ) : (
          <div style={{ color: "#aaa", fontStyle: "italic" }}>No contacts</div>
        )}
      </div>
      <span className={`chip ${TYPE_CHIPS[loc.type] || "chip-gray"}`}>{loc.type || "Other"}</span>
      <span style={{ color: "#888" }}>{loc.jobCount || 0}</span>
    </div>
  );
}

// ── Location detail (edit view) ────────────────────────────────────────────
function LocationDetail({ location, onBack, onSave, onDelete }) {
  // Migrate legacy single contactName/contactPhone fields into the
  // contacts[] array on first edit. We keep the legacy fields blank on
  // save so we have a single source of truth going forward.
  const initialContacts = (() => {
    if (Array.isArray(location.contacts) && location.contacts.length) {
      return location.contacts.map((c, i) => ({
        id: c.id || `c_${i}_${Math.random().toString(36).slice(2, 6)}`,
        name: c.name || "",
        phone: c.phone || "",
        email: c.email || "",
        role: c.role || "",
        notes: c.notes || "",
      }));
    }
    if (location.contactName || location.contactPhone) {
      return [{
        id: `c_legacy_${Math.random().toString(36).slice(2, 6)}`,
        name: location.contactName || "",
        phone: location.contactPhone || "",
        email: "",
        role: "",
        notes: "",
      }];
    }
    return [];
  })();

  const [form, setForm] = useState({
    customerName: location.customerName || "",
    name:         location.name         || "",
    address:      location.address      || "",
    lat:          location.lat          || null,
    lng:          location.lng          || null,
    type:         location.type         || "Other",
    contacts:     initialContacts,
    // Keep these in form so save can clear them — done with the
    // legacy single-contact representation.
    contactName:  "",
    contactPhone: "",
    accessNotes:  location.accessNotes  || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    // Block save if the address has been changed but isn't verified.
    // Same rule as new-location creation: address must come from
    // Google Places (lat/lng populated) so the directory stays clean.
    if (!form.address.trim()) {
      setSaveError("Address is required.");
      return;
    }
    if (typeof form.lat !== "number" || typeof form.lng !== "number") {
      setSaveError(
        "Address must be selected from the dropdown to verify it. Re-type and pick the matching suggestion."
      );
      return;
    }
    setSaveError("");
    setSaving(true);
    // Normalize contact phones to "(NNN) NNN-NNNN" on save so legacy
    // raw-typed numbers don't survive past the next edit.
    const normalized = {
      ...form,
      contacts: (form.contacts || []).map((c) => ({
        ...c,
        phone: c.phone ? formatPhone(c.phone) : "",
      })),
    };
    await onSave(location.id, normalized);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function handleDelete() {
    // Two-tier confirmation when the location has been used before — we
    // want a deliberate action, not a misclick. Existing jobs that
    // referenced this location aren't affected; only the directory entry
    // disappears (so it stops appearing in autocomplete).
    const jobCount = location.jobCount || 0;
    const warning = jobCount > 0
      ? `Delete "${location.name}"?\n\nThis location has been used on ${jobCount} past job${jobCount === 1 ? "" : "s"}. Those jobs will keep their saved address — only this directory entry is removed.\n\nProceed?`
      : `Delete "${location.name}"?`;
    if (!window.confirm(warning)) return;
    setDeleting(true);
    try {
      await onDelete(location.id);
    } catch (err) {
      console.error("Couldn't delete location", err);
      alert("Couldn't delete: " + (err?.message || err));
      setDeleting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-sm" onClick={onBack}>← Back</button>
          <div>
            <div className="page-title">{location.name}</div>
            <div className="page-subtitle">{location.address}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-sm"
            onClick={handleDelete}
            disabled={deleting}
            style={{ color: "#a32d2d", borderColor: "#e5b8b8" }}
          >
            {deleting ? "Deleting..." : "Delete location"}
          </button>
          <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
            {saved ? "Saved ✓" : saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
      <div className="page-body">
        {saveError && (
          <div style={{
            background: "#fff0f0", border: "1px solid #f5c5c5",
            borderRadius: 7, padding: "8px 12px", marginBottom: 12,
            fontSize: 12, color: "#a32d2d",
          }}>{saveError}</div>
        )}
        <div className="two-col">
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Location details</div></div>

            {/* Customer name */}
            <div className="field-row">
              <div className="field-label">Customer / company</div>
              <input
                value={form.customerName}
                onChange={(e) => set("customerName", e.target.value)}
                placeholder="e.g. Malca-Amit  (groups multiple addresses)"
                style={{ flex: 1 }}
              />
            </div>

            {/* Display name */}
            <div className="field-row">
              <div className="field-label">Display name</div>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                style={{ flex: 1 }}
              />
            </div>

            {/* Address — Places autocomplete */}
            <div className="field-row">
              <div className="field-label">
                Address
                {form.lat && (
                  <span style={{ marginLeft: 4, fontSize: 9, color: "#2d7a2d", fontWeight: 700 }}>✓ verified</span>
                )}
              </div>
              <PlacesAutocomplete
                value={form.address}
                onChange={(addr, lat, lng) => {
                  set("address", addr);
                  if (lat != null) { set("lat", lat); set("lng", lng); }
                }}
                placeholder="Start typing an address…"
                style={{ flex: 1 }}
              />
            </div>

            {/* Type */}
            <div className="field-row">
              <div className="field-label">Type</div>
              <select value={form.type} onChange={(e) => set("type", e.target.value)} style={{ flex: 1 }}>
                {TYPES.filter((t) => t !== "All").map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="panel">
            <div className="panel-header"><div className="panel-title">Access notes</div></div>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
                Shown to guards automatically on their job card
              </div>
              <textarea
                value={form.accessNotes}
                onChange={(e) => set("accessNotes", e.target.value)}
                placeholder="Gate codes, parking instructions, contact on arrival..."
                style={{ width: "100%", minHeight: 100 }}
              />
            </div>

            {form.lat && (
              <div style={{ padding: "0 14px 14px" }}>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Coordinates (for mileage)</div>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "#555" }}>
                  {form.lat.toFixed(6)}, {form.lng.toFixed(6)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contacts — multi-person directory for this location.
            Owner-managed entry point; jobs can also auto-save contacts
            here when the owner enters new ones during job creation. */}
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div className="panel-title">Contacts</div>
              <div className="panel-subtitle">
                People we've dealt with at this location — recipients, store managers, security desks.
                Pulled in automatically when creating jobs at this address.
              </div>
            </div>
            <button
              className="btn btn-sm btn-blue"
              onClick={() => set("contacts", [
                ...form.contacts,
                {
                  id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                  name: "", phone: "", email: "", role: "", notes: "",
                },
              ])}
            >
              + Add contact
            </button>
          </div>
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {form.contacts.length === 0 && (
              <div style={{ fontSize: 11, color: "#aaa", fontStyle: "italic" }}>
                No contacts on file. Click "+ Add contact" to start, or they'll auto-save when entered on a job.
              </div>
            )}
            {form.contacts.map((c, idx) => (
              <ContactRow
                key={c.id}
                contact={c}
                onChange={(patch) => set("contacts", form.contacts.map((cc, i) => i === idx ? { ...cc, ...patch } : cc))}
                onRemove={() => set("contacts", form.contacts.filter((_, i) => i !== idx))}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable contact-row editor used in LocationDetail and AddLocationModal.
function ContactRow({ contact, onChange, onRemove }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr 140px 32px",
      gap: 8, alignItems: "start",
      padding: "10px 12px", border: "1px solid #e8e8e8", borderRadius: 8,
      background: "#fafbfc",
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <input
          value={contact.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Contact name"
          style={{ fontSize: 12, padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6 }}
        />
        <input
          value={contact.role}
          onChange={(e) => onChange({ role: e.target.value })}
          placeholder="Role (optional) — e.g. Store manager, Recipient"
          style={{ fontSize: 11, padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6 }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <PhoneInput
          value={contact.phone}
          onChange={(v) => onChange({ phone: v })}
          style={{ fontSize: 12, padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6 }}
        />
        <input
          type="email"
          value={contact.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="Email (optional)"
          style={{ fontSize: 11, padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6 }}
        />
      </div>
      <input
        value={contact.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Notes (e.g. ext 4, Tues only)"
        style={{ fontSize: 11, padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6 }}
      />
      <button
        onClick={onRemove}
        title="Remove contact"
        style={{
          background: "none", border: "1px solid #eee",
          color: "#888", borderRadius: 6, fontSize: 14,
          padding: "4px 8px", cursor: "pointer", height: 28, alignSelf: "center",
        }}
      >✕</button>
    </div>
  );
}

// ── Add location modal ─────────────────────────────────────────────────────
function AddLocationModal({ companyId, onClose, onCreated }) {
  const [form, setForm] = useState({
    customerName: "",
    name:         "",
    address:      "",
    lat:          null,
    lng:          null,
    type:         "Other",
    // contacts[] is the new schema; legacy contactName/contactPhone are
    // omitted on create (cleared on save in LocationDetail too).
    contacts:     [],
    accessNotes:  "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.name.trim()) return setError("Display name is required.");
    // Block save unless the address was picked from Google Places
    // (lat/lng populated). Free-typed addresses can have typos /
    // wrong city / unparseable formatting and pollute the directory.
    // Owner must select from the dropdown to commit.
    if (!form.address.trim()) return setError("Address is required.");
    if (typeof form.lat !== "number" || typeof form.lng !== "number") {
      return setError(
        "Address must be selected from the dropdown to verify it. Re-type and pick the matching suggestion."
      );
    }
    setSaving(true);
    try {
      // Strip empty contacts before saving — no point persisting blank
      // rows the owner half-typed and abandoned.
      const cleanContacts = (form.contacts || [])
        .filter((c) => c.name?.trim() || c.phone?.trim() || c.email?.trim())
        // Normalize phones on save so the stored format is canonical.
        .map((c) => ({ ...c, phone: c.phone ? formatPhone(c.phone) : "" }));
      const payload = { ...form, contacts: cleanContacts, companyId };
      const ref = await createLocation(payload);
      onCreated({ id: ref.id, ...payload, jobCount: 0 });
    } catch {
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 500, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Add location</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          {error && (
            <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#a32d2d" }}>
              {error}
            </div>
          )}

          {/* Customer name */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>
              Customer / company
              <span style={{ fontWeight: 400, color: "#999", marginLeft: 4 }}>(optional — groups multiple addresses)</span>
            </label>
            <input
              value={form.customerName}
              onChange={(e) => set("customerName", e.target.value)}
              placeholder="e.g. Malca-Amit"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Display name *</label>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Malca-Amit CLT"
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Type</label>
              <select value={form.type} onChange={(e) => set("type", e.target.value)}>
                {TYPES.filter((t) => t !== "All").map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Address — Places autocomplete */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>
              Address
              {form.lat && <span style={{ marginLeft: 6, color: "#2d7a2d" }}>✓ verified</span>}
            </label>
            <PlacesAutocomplete
              value={form.address}
              onChange={(addr, lat, lng) => {
                set("address", addr);
                if (lat != null) { set("lat", lat); set("lng", lng); }
              }}
              placeholder="Start typing an address…"
            />
          </div>

          {/* Contacts — multiple people per location. Empty by default;
              "+ Add contact" reveals a row with name/phone/email/role. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>
                Contacts
                <span style={{ fontWeight: 400, color: "#999", marginLeft: 4 }}>(optional)</span>
              </label>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => set("contacts", [
                  ...form.contacts,
                  {
                    id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                    name: "", phone: "", email: "", role: "", notes: "",
                  },
                ])}
                style={{ fontSize: 10 }}
              >
                + Add contact
              </button>
            </div>
            {form.contacts.length === 0 ? (
              <div style={{ fontSize: 10, color: "#aaa", fontStyle: "italic", padding: "4px 0" }}>
                No contacts yet — add one now or anytime later from the location's detail view.
              </div>
            ) : (
              form.contacts.map((c, idx) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  onChange={(patch) => set("contacts", form.contacts.map((cc, i) => i === idx ? { ...cc, ...patch } : cc))}
                  onRemove={() => set("contacts", form.contacts.filter((_, i) => i !== idx))}
                />
              ))
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Access notes</label>
            <textarea
              value={form.accessNotes}
              onChange={(e) => set("accessNotes", e.target.value)}
              placeholder="Gate codes, parking, arrival instructions..."
              style={{ minHeight: 70 }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Add location"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate review / merge tool ──────────────────────────────────────────
function DuplicateReview({ companyId, groups, onBack, manual }) {
  const [survivors, setSurvivors] = useState({}); // groupKey -> chosen survivor id
  const [skipped, setSkipped]     = useState({}); // groupKey -> Set of ids left out of the merge
  const [busyKey, setBusyKey]     = useState(null);
  const [results, setResults]     = useState({}); // groupKey -> message

  const keyFor = (g) => g.map((l) => l.id).sort().join("|");

  // Default survivor: prefer one with a Place ID, then most-used (jobCount),
  // then the most complete address.
  function defaultSurvivor(g) {
    return g.slice().sort((a, b) => {
      const ap = a.placeId ? 1 : 0;
      const bp = b.placeId ? 1 : 0;
      if (ap !== bp) return bp - ap;
      const aj = a.jobCount || 0;
      const bj = b.jobCount || 0;
      if (aj !== bj) return bj - aj;
      return (b.address || "").length - (a.address || "").length;
    })[0].id;
  }

  async function handleMerge(g) {
    const gk = keyFor(g);
    const survivorId = survivors[gk] || defaultSurvivor(g);
    const skip = skipped[gk] || new Set();
    const dupIds = g.map((l) => l.id).filter((id) => id !== survivorId && !skip.has(id));
    const survivorName = g.find((l) => l.id === survivorId)?.name || "this location";
    if (!window.confirm(
      `Merge ${dupIds.length} duplicate${dupIds.length === 1 ? "" : "s"} into "${survivorName}"?\n\n` +
      "Their contacts, job counts, and billing recall history move to the surviving " +
      "location, and any jobs pointing at the duplicates are repointed. The duplicates " +
      "are then deleted. This can't be undone."
    )) return;
    setBusyKey(gk);
    try {
      const { jobsRepointed } = await mergeLocations(companyId, survivorId, dupIds);
      setResults((r) => ({ ...r, [gk]: `Merged — ${jobsRepointed} job${jobsRepointed === 1 ? "" : "s"} repointed.` }));
    } catch (err) {
      console.error("Merge failed", err);
      setResults((r) => ({ ...r, [gk]: `Merge failed: ${err?.message || err}` }));
    }
    setBusyKey(null);
  }

  // "These are NOT duplicates" — persist the owner's call so this group
  // is never suggested again. Writes notSameAs (the other members' ids)
  // onto every location in the group; findDuplicateGroups skips pairs
  // marked that way. A new location matching one of them still surfaces.
  async function handleDismiss(g) {
    const gk = keyFor(g);
    if (!window.confirm(
      `Keep these ${g.length} locations as separate places?\n\n` +
      "They won't be suggested for merging again."
    )) return;
    setBusyKey(gk);
    try {
      const ids = g.map((l) => l.id);
      for (const loc of g) {
        const others = ids.filter((id) => id !== loc.id);
        const next = Array.from(new Set([...(loc.notSameAs || []), ...others]));
        await updateLocation(loc.id, { notSameAs: next });
      }
      setResults((r) => ({ ...r, [gk]: "Dismissed" }));
    } catch (err) {
      console.error("Dismiss failed", err);
      setResults((r) => ({ ...r, [gk]: `Dismiss failed: ${err?.message || err}` }));
    }
    setBusyKey(null);
  }

  // Drop groups already merged or dismissed this session for instant
  // feedback (the parent's watchLocations also re-derives groups when it
  // reconciles — dismissed pairs then stay gone via notSameAs).
  const visibleGroups = groups.filter((g) => {
    const msg = results[keyFor(g)];
    return !(msg && (msg.startsWith("Merged") || msg === "Dismissed"));
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-sm" onClick={onBack}>← Back</button>
          <div>
            <div className="page-title">{manual ? "Merge selected locations" : "Merge duplicate locations"}</div>
            <div className="page-subtitle">
              {manual
                ? `${groups[0].length} locations selected`
                : `${visibleGroups.length} group${visibleGroups.length !== 1 ? "s" : ""} of likely duplicates`}
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        {visibleGroups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <div>{manual ? "Merged" : "No duplicate locations"}</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              Tick <b>Merge</b> on the entries to combine, and pick the one to <b>Keep</b>. The ticked
              ones fold into it — contacts, job history, and billing recall move over, and existing
              jobs are repointed. Unticked entries are left as they are.
            </div>
            {visibleGroups.map((g) => {
              const gk = keyFor(g);
              const chosen = survivors[gk] || defaultSurvivor(g);
              const skip = skipped[gk] || new Set();
              const mergeCount = g.filter((l) => l.id !== chosen && !skip.has(l.id)).length;
              const msg = results[gk];
              const toggleSkip = (id) => setSkipped((s) => {
                const next = new Set(s[gk] || []);
                if (next.has(id)) next.delete(id); else next.add(id);
                return { ...s, [gk]: next };
              });
              return (
                <div key={gk} className="panel" style={{ padding: 0 }}>
                  <div style={{ padding: "8px 14px", background: "#fff7e6", borderBottom: "1px solid #f0e0c0", fontSize: 11, fontWeight: 700, color: "#8a5a00" }}>
                    {manual ? `${g.length} selected locations` : `${g.length} possible duplicates`}
                  </div>
                  <div style={{
                    display: "grid", gridTemplateColumns: "44px 44px 1fr 70px",
                    gap: 10, padding: "6px 14px", background: "#f8f8f8",
                    borderBottom: "1px solid #e8e8e8", fontSize: 10, fontWeight: 700, color: "#888",
                  }}>
                    <span style={{ textAlign: "center" }}>Merge</span>
                    <span style={{ textAlign: "center" }}>Keep</span>
                    <span>Location</span>
                    <span />
                  </div>
                  {g.map((loc) => {
                    const isKeep = chosen === loc.id;
                    const included = isKeep || !skip.has(loc.id);
                    return (
                    <div key={loc.id} style={{
                      display: "grid", gridTemplateColumns: "44px 44px 1fr 70px",
                      gap: 10, alignItems: "center", padding: "10px 14px",
                      borderBottom: "1px solid #f0f0f0",
                      background: isKeep ? "#f3fbf5" : "#fff",
                      opacity: included ? 1 : 0.5,
                    }}>
                      <input
                        type="checkbox"
                        title={isKeep ? "This is the location being kept" : "Include in this merge"}
                        checked={included}
                        disabled={isKeep}
                        onChange={() => toggleSkip(loc.id)}
                        style={{ justifySelf: "center", width: 18, height: 18, cursor: isKeep ? "default" : "pointer" }}
                      />
                      <input
                        type="radio"
                        name={`surv_${gk}`}
                        title="Keep this one — the others merge into it"
                        checked={isKeep}
                        onChange={() => {
                          setSurvivors((s) => ({ ...s, [gk]: loc.id }));
                          if (skip.has(loc.id)) toggleSkip(loc.id);
                        }}
                        style={{ justifySelf: "center", width: 18, height: 18, cursor: "pointer" }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>
                          {loc.name}
                          {isKeep && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: "#2d7a2d" }}>KEEP</span>}
                          {!included && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: "#888" }}>LEAVE AS IS</span>}
                          {loc.placeId && <span style={{ marginLeft: 6, fontSize: 9, color: "#185FA5" }}>has Place ID</span>}
                        </div>
                        <div style={{ fontSize: 10, color: "#888" }}>{loc.address || "—"}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "#888", textAlign: "right" }}>
                        {loc.jobCount || 0} job{(loc.jobCount || 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                    );
                  })}
                  <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      className="btn btn-sm btn-blue"
                      onClick={() => handleMerge(g)}
                      disabled={busyKey === gk || mergeCount === 0}
                    >
                      {busyKey === gk ? "Merging…" : `Merge ${mergeCount} into kept location`}
                    </button>
                    {!manual && (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleDismiss(g)}
                        disabled={busyKey === gk}
                        title="These are different places — stop suggesting this merge"
                      >
                        Not duplicates — keep separate
                      </button>
                    )}
                    {msg && (
                      <span style={{ fontSize: 11, color: msg.startsWith("Merge failed") ? "#a32d2d" : "#2d7a2d" }}>
                        {msg}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
